import styled from "@emotion/styled";
import { ThemeKey } from "@smartsheet/design-tokens";
import { AlertErrorIcon, AlertWarningIcon, InfoFilledIcon } from "@smartsheet/lodestar-icons";
import { RequiredThemeKeys, themed } from "@smartsheet/sds-theme-internal";
import { ExclamationOnDotIcon, InfoFilledIcon as UrsaInfoIcon, WarningFilledIcon } from "@smartsheet/ursa-icons";
import React, { forwardRef, useCallback } from "react";
import { useTheme } from "../../../theme/utils/useTheme";
import { Avatar, AvatarProps } from "../../avatar";
import { BaseChip, BaseChipProps } from "../components/BaseChip";
import { AvatarWrapper } from "../styled/AvatarWrapper";
import { avatarChipTheme, chipTheme } from "../styled/chipTheme";
import { UserChipType, SharedChipProps, ChipColorVariant } from "../types";

export interface AvatarChipProps extends SharedChipProps {
    /**
     * Props for rendering the Avatar left of the chip text.
     */
    avatarProps: Omit<AvatarProps, "tabIndex" | "size">;
    chipType?: UserChipType;
}

interface StyledBaseChipProps extends BaseChipProps {
    size: "xsmall" | "small" | "medium";
    themeMode: RequiredThemeKeys;
}

const StyledBaseChip = styled(BaseChip)<StyledBaseChipProps>(
    ({ themeMode, size }) => `
    border-radius: ${avatarChipTheme.borderRadius(themeMode)};
    padding-left: ${avatarChipTheme.padding.left[size](themeMode)};
    padding-right: ${avatarChipTheme.padding.right[size](themeMode)};
     // set the chip gap to be larger by increasing padding on the label (uses 1 value for the gap vs. the avatar having its own padding)
    & > span {
        padding-left: calc(${
            size ? chipTheme.padding.left[size](themeMode) : "0"
        } + ${avatarChipTheme.avatar.padding.right(themeMode)});
    }
`
);

export const AvatarChip = forwardRef<HTMLDivElement, AvatarChipProps>(
    ({ avatarProps, chipType = "default", overrides, ...rest }, ref) => {
        const { key } = useTheme();
        const themeMode = key as ThemeKey;

        const getChipBaseColor = useCallback(
            (chipType: UserChipType) => {
                return chipTheme.chip.color[chipType].color(themeMode);
            },
            [themeMode]
        );

        const icons = themed({
            info: {
                "lodestar-light": <InfoFilledIcon size={"xSmall"} />,
                "ursa-light": <UrsaInfoIcon size={16} color="currentColor" />,
            },
            warning: {
                "lodestar-light": <AlertWarningIcon size={"xSmall"} />,
                "ursa-light": <WarningFilledIcon size={16} color="currentColor" />,
            },
            error: {
                "lodestar-light": <AlertErrorIcon size={"xSmall"} />,
                "ursa-light": <ExclamationOnDotIcon size={16} color="currentColor" />,
            },
            default: {
                "lodestar-light": avatarProps.content,
                "ursa-light": avatarProps.content,
            },
        });

        const updatedAvatarProps = {
            ...avatarProps,
            src: chipType === "default" ? avatarProps.src : undefined,
            email: chipType === "default" ? avatarProps.email : undefined,
            content: icons[chipType](themeMode),
            color:
                chipType === "default"
                    ? avatarProps.color
                    : {
                          backgroundColor: "transparent",
                          textColor: "auto",
                      },
        };

        return (
            <StyledBaseChip
                {...rest}
                ref={ref}
                color={getChipBaseColor(chipType) as ChipColorVariant}
                themeMode={themeMode}
                size={rest.size || chipTheme.chip.size(themeMode)}
                contentBefore={
                    <AvatarWrapper themeMode={themeMode}>
                        <Avatar {...updatedAvatarProps} aria-hidden role="presentation" tabIndex={-1} size="xxSmall" />
                    </AvatarWrapper>
                }
                overrides={overrides}
            />
        );
    }
);

AvatarChip.displayName = "AvatarChip";
